/**
 * One icon family, drawn to a single spec so they sit together without one looking
 * heavier or larger than its neighbours:
 *
 *   24x24 viewBox, 2px stroke, round caps and joins, no fills, currentColor.
 *   Geometry stays inside a 20x20 optical box (2px margin) so a circle and a
 *   square read as the same size -- a full-bleed square always looks bigger than
 *   a full-bleed circle otherwise.
 *
 * Inline SVG, never an icon font and never a CDN: the CSP forbids external hosts,
 * and a font is a second request that can fail on its own and leave boxes.
 *
 * The previous set was drawn from memory and it showed -- the gear was a single
 * distorted path and the balance scale did not read as a scale at all. These are
 * built from primitives (circles, lines, simple arcs) precisely because those are
 * hard to get subtly wrong.
 *
 * Keys match core.modules.icon, so adding a module means adding an icon here.
 */

import type { ReactElement } from "react";

interface IconProps {
  name: string;
  /** Optical size in px. 20 is the UI default; the rail uses 22. */
  size?: number;
}

const STROKE = 2;

function svg(children: ReactElement, size: number) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={STROKE}
      strokeLinecap="round"
      strokeLinejoin="round"
      focusable="false"
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

/* Each entry is the inner geometry only. Keeping them as fragments rather than
 * whole <svg> elements is what guarantees every icon shares the same viewBox and
 * stroke -- the old set let each icon declare its own and they drifted. */
const GEOMETRY: Record<string, ReactElement> = {
  // Dashboard: a house. Roof as one open polyline, body as a second, so the
  // eaves stay crisp instead of forming a filled wedge.
  home: (
    <>
      <path d="M3 10.2 12 3l9 7.2" />
      <path d="M5.5 8.8V20a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1V8.8" />
      <path d="M9.8 21v-6.2h4.4V21" />
    </>
  ),

  // Reconciliation: a balance scale. Centre post, beam, two pans drawn as arcs
  // hanging from the beam ends -- the previous version drew triangles, which read
  // as mountains rather than pans.
  scale: (
    <>
      <path d="M12 4.2v15.4" />
      <path d="M8.6 20.2h6.8" />
      <path d="M4.5 8.1h15" />
      <path d="M4.5 8.1 2 13.4a3.4 3.4 0 0 0 5 0z" />
      <path d="M19.5 8.1 17 13.4a3.4 3.4 0 0 0 5 0z" />
      <circle cx="12" cy="5.4" r="1.4" />
    </>
  ),

  // Invoice Reader: a document with text lines and a folded corner. Reads as
  // "document" far more reliably than the zigzag-bottom receipt shape did.
  receipt: (
    <>
      <path d="M14 3H7a1.8 1.8 0 0 0-1.8 1.8v14.4A1.8 1.8 0 0 0 7 21h10a1.8 1.8 0 0 0 1.8-1.8V7.8z" />
      <path d="M14 3v4.8h4.8" />
      <path d="M8.6 12.4h6.8M8.6 16h4.6" />
    </>
  ),

  // Administration: a gear as a circle plus eight radial teeth. Built from
  // primitives on purpose -- the single-path gear it replaces was visibly lopsided.
  settings: (
    <>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.6v2.6M12 18.8v2.6M2.6 12h2.6M18.8 12h2.6" />
      <path d="M5.4 5.4l1.9 1.9M16.7 16.7l1.9 1.9M18.6 5.4l-1.9 1.9M7.3 16.7l-1.9 1.9" />
    </>
  ),

  // Edit Invoice: a document with a pencil. Drawn as the document opening on the right
  // so the pencil occupies that corner rather than crossing the page and muddling both
  // shapes. Replaced a generic bulleted list, which said "a list of things" and nothing
  // about editing an invoice.
  "file-edit": (
    <>
      <path d="M13.4 3H6.9A1.9 1.9 0 0 0 5 4.9v14.2A1.9 1.9 0 0 0 6.9 21h4.3" />
      <path d="M13.4 3v4.8h4.8" />
      <path d="M8.6 11.6h5.2M8.6 15.2h3" />
      <path d="M19.6 13.2l1.6 1.6-5 5-2.2.6.6-2.2z" />
    </>
  ),

  users: (
    <>
      <circle cx="9.2" cy="8.4" r="3.4" />
      <path d="M2.8 20.2a6.4 6.4 0 0 1 12.8 0" />
      <path d="M16.4 5.4a3.4 3.4 0 0 1 0 6M17.8 14.6a5.6 5.6 0 0 1 3.4 5.6" />
    </>
  ),

  shield: (
    <>
      <path d="M12 3.2 19 6v5.6c0 4.2-2.9 7.6-7 9.2-4.1-1.6-7-5-7-9.2V6z" />
      <path d="M9.4 11.9l1.9 1.9 3.5-3.5" />
    </>
  ),

  list: (
    <>
      <path d="M9 6.4h11.2M9 12h11.2M9 17.6h11.2" />
      <circle cx="4.4" cy="6.4" r="1.1" />
      <circle cx="4.4" cy="12" r="1.1" />
      <circle cx="4.4" cy="17.6" r="1.1" />
    </>
  ),

  logout: (
    <>
      <path d="M14.6 3.4h2.8A2 2 0 0 1 19.4 5.4v13.2a2 2 0 0 1-2 2h-2.8" />
      <path d="M9.6 16.6 5 12l4.6-4.6" />
      <path d="M5 12h9" />
    </>
  ),

  chevron: <path d="M9.4 5.6 15.8 12l-6.4 6.4" />,

  sun: (
    <>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.8v2.2M12 19v2.2M2.8 12h2.2M19 12h2.2" />
      <path d="M5.6 5.6l1.6 1.6M16.8 16.8l1.6 1.6M18.4 5.6l-1.6 1.6M7.2 16.8l-1.6 1.6" />
    </>
  ),

  // A crescent as a single closed path. A moon carved out of a circle needs a
  // mask, which cannot inherit currentColor cleanly across themes.
  moon: <path d="M20 14.4A8.4 8.4 0 0 1 9.6 4a8.4 8.4 0 1 0 10.4 10.4z" />,

  // Language: a globe with a meridian and the equator. The vertical ellipse is
  // what makes it read as a globe rather than a clock.
  language: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <path d="M12 3c2.4 2.5 3.7 5.6 3.7 9s-1.3 6.5-3.7 9c-2.4-2.5-3.7-5.6-3.7-9S9.6 5.5 12 3z" />
    </>
  ),

  check: <path d="M4.8 12.6l4.6 4.6L19.2 7.4" />,

  plus: <path d="M12 5.2v13.6M5.2 12h13.6" />,

  // Analysis: an axis with three rising bars. A chart is what reads as "analysis" at
  // 22px -- a magnifier over data was tried and fails, because the lens and the data
  // it magnifies compete for the same 20px box. Bars sit ON the axis; the round caps
  // make that contact look deliberate rather than like a near miss.
  chart: (
    <>
      <path d="M4.4 4.4v15.2h15.2" />
      <path d="M9 19.6v-4.6" />
      <path d="M13.4 19.6v-7.4" />
      <path d="M17.8 19.6v-10.4" />
    </>
  ),

  // Sales (module key `grooming`): a shopping bag -- lucide's `shopping-bag` geometry,
  // which already sits in this family's 20x20 box (x 3-21, y 2-22) with round joins.
  // The tab is the shops' sales, their daily form and commission, not grooming alone;
  // the scissors it had before read as the Grooming page only (the owner, 27 Sep 2026).
  // The Grooming page inside the module keeps its own scissors.
  //
  // The KEY stays `scissors`: it is the name `core.modules.icon` holds for the tab, and
  // renaming it would take a shell migration after which a redeploy of any earlier
  // commit draws the square fallback here and in module-chrome.js. The same three paths
  // are in `packages/petpoint-shell-ui/src/module-chrome.js`, and
  // `backend/tests/test_module_chrome.py` holds the two together.
  scissors: (
    <>
      <path d="M16 10a4 4 0 0 1-8 0" />
      <path d="M3.103 6.034h17.794" />
      <path d="M3.4 5.467a2 2 0 0 0-.4 1.2V20a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6.667a2 2 0 0 0-.4-1.2l-2-2.667A2 2 0 0 0 17 2H7a2 2 0 0 0-1.6.8z" />
    </>
  ),

  square: <rect x="4.4" y="4.4" width="15.2" height="15.2" rx="2.6" />,
};

export function Icon({ name, size = 20 }: IconProps) {
  return svg(GEOMETRY[name] ?? GEOMETRY.square!, size);
}

export const iconNames = Object.keys(GEOMETRY);
